import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { ItemCard, ItemCardDivider, ItemCardFooter } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import '@sola/locales/formatters.ts';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet.tsx';
import { CommitmentCardInfoSection } from '../commitment-card-info-section/commitment-card-info-section.tsx';
export type CommitmentCardProps = {
  commitment: GraphQLCommitment;
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
};
const CommitmentCardPaymentDate: React.FC<GraphQLCommitment> = t0 => {
  const $ = _c(11);
  const {
    isRecurring,
    paymentDateNext,
    paymentDatePending
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "common"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  if (!isRecurring) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ArkIcon iconName="alert-triangle" className="mr-snug-lg" size="1.25rem" />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== t) {
      t3 = t("my:commitments.notRecurring.title");
      $[2] = t;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t3) {
      t4 = <>{t2}<span>{t3}</span></>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (paymentDateNext) {
    let t2;
    if ($[6] !== paymentDateNext || $[7] !== t) {
      t2 = t("my:commitments.nextPayment", {
        date: paymentDateNext
      });
      $[6] = paymentDateNext;
      $[7] = t;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  if (paymentDatePending) {
    let t2;
    if ($[9] !== t) {
      t2 = t("my:commitments.nextPaymentPending");
      $[9] = t;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  return null;
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const CommitmentCard: React.FC<CommitmentCardProps> = props => {
  const $ = _c(26);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    commitment,
    isActive,
    onActiveChange
  } = props;
  let t1;
  if ($[1] !== commitment) {
    t1 = <CommitmentCardInfoSection commitment={commitment} />;
    $[1] = commitment;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ItemCardDivider />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !commitment.isRecurring;
  let t4;
  if ($[4] !== t3) {
    t4 = cn("flex items-center label-moderate label-neutral-mild label-sm md:label-md", {
      "label-warning": t3
    });
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== commitment) {
    t5 = <CommitmentCardPaymentDate {...commitment} />;
    $[6] = commitment;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("my:commitments.detailsSheet.triggerLabel");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <div className="ml-auto"><LinkStyle noUnderline={true} noPadding={true} inline={true} variant="primary" size="lg"><button type="button" className="CommitmentCard__trigger">{t7}</button></LinkStyle></div>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== commitment || $[16] !== isActive || $[17] !== onActiveChange || $[18] !== t8) {
    t9 = <CommitmentDetailsSheet commitment={commitment} isOpen={isActive} onOpenChange={onActiveChange} openTrigger={t8} />;
    $[15] = commitment;
    $[16] = isActive;
    $[17] = onActiveChange;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t9) {
    t10 = <ItemCardFooter justify="between">{t6}{t9}</ItemCardFooter>;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t1 || $[24] !== t10) {
    t11 = <ItemCard>{t1}{t2}{t10}</ItemCard>;
    $[23] = t1;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};